import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { brandingApi } from '../services/api'
import { DEFAULT_BRANDING_ENV } from '../config/runtimeConfig'

const BrandingContext = createContext(null)

// Eagerly bundles every image in src/assets so the correct default logo/
// favicon for this environment can be resolved by filename at runtime.
// A plain runtime string path (e.g. '/src/assets/foo.png') would 404 in a
// production build, since only the fingerprinted /assets/ output exists
// there - this glob ensures Vite actually bundles the file so it works.
const ASSET_MODULES = import.meta.glob('../assets/*.{png,jpg,jpeg,svg,webp}', { eager: true, import: 'default' })
function resolveDefaultAsset(filename, fallbackFilename) {
  return ASSET_MODULES[`../assets/${filename}`] || ASSET_MODULES[`../assets/${fallbackFilename}`] || null
}
const XTRACT_LOGO_DARK_FILE = 'xtract-symbol-dark.png'
const XTRACT_LOGO_LIGHT_FILE = 'xtract-symbol-light.png'
const XTRACT_FAVICON_LIGHT_FILE = 'xtract-favicon-light.png'
const XTRACT_FAVICON_DARK_FILE = 'xtract-favicon-dark.png'
const DEFAULT_LOGO_FILE = DEFAULT_BRANDING_ENV.logoFile || XTRACT_LOGO_DARK_FILE
const DEFAULT_FAVICON_FILE = DEFAULT_BRANDING_ENV.faviconFile || XTRACT_FAVICON_LIGHT_FILE

// The Xtract symbol comes in a dark-surface and a light-surface version. The
// sidebar and login panel are always dark, so the default logo is the dark one;
// surfaces that follow the theme use this to get the light version in light mode.
const XTRACT_LOGO_DARK = ASSET_MODULES[`../assets/${XTRACT_LOGO_DARK_FILE}`]
const XTRACT_LOGO_LIGHT = ASSET_MODULES[`../assets/${XTRACT_LOGO_LIGHT_FILE}`]
const XTRACT_FAVICON_URLS = new Set([
  ASSET_MODULES[`../assets/${XTRACT_FAVICON_LIGHT_FILE}`],
  ASSET_MODULES[`../assets/${XTRACT_FAVICON_DARK_FILE}`],
].filter(Boolean))

/** Light-surface version of `url` when it is the dark Xtract symbol, else `url` unchanged (client and uploaded logos). */
export function logoForLightSurface(url) {
  return url && url === XTRACT_LOGO_DARK && XTRACT_LOGO_LIGHT ? XTRACT_LOGO_LIGHT : url
}

// Every file that is Xtract's own logo, whatever this environment's default
// branding is. Decides useIsXtractBrand.
const XTRACT_LOGOS = new Set([
  XTRACT_LOGO_DARK,
  XTRACT_LOGO_LIGHT,
].filter(Boolean))

// Default branding shown before login and before the branding API responds.
// Sourced from the container's runtime config (XTRACT_DEFAULT_* env vars, see
// config/runtimeConfig.js) with the build-time VITE_DEFAULT_* values as a
// fallback, so one image serves every deployment - only the injected values
// differ. Falls back to Xtract's own defaults if neither is set (e.g. local dev).
export const BRANDING_DEFAULTS = {
  app_name: DEFAULT_BRANDING_ENV.appName || 'Xtract',
  subtitle: DEFAULT_BRANDING_ENV.subtitle || 'Synapx',
  logo_url: resolveDefaultAsset(DEFAULT_LOGO_FILE, XTRACT_LOGO_DARK_FILE),
  favicon_url: resolveDefaultAsset(DEFAULT_FAVICON_FILE, XTRACT_FAVICON_LIGHT_FILE),
  primary_color: DEFAULT_BRANDING_ENV.primaryColor || '#1e2a3b',
  accent_color: DEFAULT_BRANDING_ENV.accentColor || '#2563eb',
  login_tagline: null,
  is_default: true,
  apply_to_plugin: false,
}

// Theme token defaults (neutral Xtract baseline)
export const THEME_TOKEN_DEFAULTS = {
  primary: '#334E68',
  primary_hover: '#243B53',
  primary_active: '#102A43',
  primary_text: '#FFFFFF',
  background: '#1E2A3B',
  surface: '#FFFFFF',
  border: '#D9E2EC',
  text_primary: '#102A43',
  text_secondary: '#486581',
  text_inverse: '#FFFFFF',
  success: '#3b9b5e',
  warning: '#D9C800',
  error: '#d94a44',
  info: '#2563EB',
  toggle_on: '#1E2A3B',
  toggle_off: '#d1d5db',
}

/**
 * Inject branding colors as CSS custom properties on :root
 * so Tailwind/CSS classes can reference var(--brand-primary) etc.
 */
function applyBrandColors(primary, accent) {
  const root = document.documentElement
  root.style.setProperty('--brand-primary', primary || BRANDING_DEFAULTS.primary_color)
  root.style.setProperty('--brand-accent', accent || BRANDING_DEFAULTS.accent_color)

  // Pre-compute lighter/darker shades for hover states
  root.style.setProperty('--brand-primary-hover', darkenHex(primary || BRANDING_DEFAULTS.primary_color, 15))
  root.style.setProperty('--brand-primary-light', lightenHex(primary || BRANDING_DEFAULTS.primary_color, 90))

  // Recolour the portal-wide "navy" ramp from the brand colour so every navy-*
  // usage (buttons, focus rings, active nav, badges, icon highlights) follows it.
  applyDerivedNavyScale(primary || BRANDING_DEFAULTS.primary_color)

  // Keep dark-mode surface fills neutral; do not tint page/card backgrounds from
  // the brand/nav color.
  applyNeutralSurfaceScale()
}

// Interim AI-accent gradient (turquoise/teal) used for AI-processing UI
// (extraction progress bar, "AI Suggest" button border). Matches the Xtract
// logo mark. Used as the fallback when no logo is uploaded, or when colour
// extraction from an uploaded logo fails / yields nothing usable.
const AI_GRADIENT_FROM = '#0f766e'
const AI_GRADIENT_TO = '#2dd4bf'

// Cache extracted-gradient results per logo URL so we don't reprocess the same
// image (canvas sampling) on every branding refresh/apply within a session.
const _logoGradientCache = new Map()

/**
 * Sample an uploaded logo image and derive a 2-stop gradient from its most
 * prominent colours, for use in AI-processing UI (extraction progress bar,
 * "AI Suggest" button border, AI badges, etc). This keeps that UI visually
 * tied to the org's own brand mark rather than a fixed Xtract teal.
 *
 * Quality handling: raw dominant colours from a logo can be too close to each
 * other (near-monochrome logo), too washed out/desaturated (greys, off-whites,
 * near-black), or otherwise a poor gradient pair. When that happens we fall
 * back to deriving a gradient from *shades* of the single most vivid colour
 * found (lighter/darker HSL steps of the same hue) instead of two unrelated
 * sampled colours, so the gradient still looks intentional.
 *
 * Resolves to { from, to } hex strings. Never rejects - falls back to the
 * fixed Xtract teal pair on any failure (missing logo, load error, CORS, no
 * usable colour found).
 */
function extractLogoGradient(logoUrl) {
  if (!logoUrl) return Promise.resolve({ from: AI_GRADIENT_FROM, to: AI_GRADIENT_TO })
  if (_logoGradientCache.has(logoUrl)) return Promise.resolve(_logoGradientCache.get(logoUrl))

  return new Promise((resolve) => {
    const fallback = { from: AI_GRADIENT_FROM, to: AI_GRADIENT_TO }
    try {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      const finish = (result) => {
        _logoGradientCache.set(logoUrl, result)
        resolve(result)
      }
      img.onload = () => {
        try {
          const SIZE = 48 // downsample for fast, stable sampling
          const canvas = document.createElement('canvas')
          canvas.width = SIZE
          canvas.height = SIZE
          const ctx = canvas.getContext('2d', { willReadFrequently: true })
          ctx.drawImage(img, 0, 0, SIZE, SIZE)
          const { data } = ctx.getImageData(0, 0, SIZE, SIZE)

          // Bucket pixels by quantised hue+saturation+lightness so visually
          // similar pixels group together, skipping transparent/near-white/
          // near-black/near-grey pixels (logo backgrounds, not brand colour).
          const buckets = new Map() // key -> { count, r, g, b, s, l }
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3]
            if (a < 200) continue
            const [h, s, l] = rgbToHsl(r, g, b)
            if (l > 0.94 || l < 0.06) continue // near-white / near-black
            if (s < 0.12) continue             // near-grey (unsaturated)
            const key = `${Math.round(h / 12)}-${Math.round(s * 4)}-${Math.round(l * 4)}`
            const bucket = buckets.get(key) || { count: 0, r: 0, g: 0, b: 0 }
            bucket.count += 1
            bucket.r += r; bucket.g += g; bucket.b += b
            buckets.set(key, bucket)
          }

          const ranked = Array.from(buckets.values())
            .map((bucket) => ({
              count: bucket.count,
              r: Math.round(bucket.r / bucket.count),
              g: Math.round(bucket.g / bucket.count),
              b: Math.round(bucket.b / bucket.count),
            }))
            .sort((a, b) => b.count - a.count)

          if (ranked.length === 0) return finish(fallback)

          const toHsl = (c) => rgbToHsl(c.r, c.g, c.b)
          const toHex = (c) => `#${((1 << 24) + (c.r << 16) + (c.g << 8) + c.b).toString(16).slice(1)}`
          const primaryHsl = toHsl(ranked[0])

          // Look for a second dominant colour that's meaningfully different in
          // hue from the first (a real second brand colour, not a near-dupe).
          const secondary = ranked.slice(1).find((c) => {
            const [h] = toHsl(c)
            let diff = Math.abs(h - primaryHsl[0])
            if (diff > 180) diff = 360 - diff
            return diff > 25
          })

          let fromHex, toHex_
          if (secondary) {
            // Two distinct sampled colours - use them directly as the gradient.
            fromHex = toHex(ranked[0])
            toHex_ = toHex(secondary)
          } else {
            // Logo colours are too close together / effectively one colour -
            // fall back to shades of that single colour so the gradient still
            // reads as deliberate rather than a flat, muddy pair.
            const [h, s, l] = primaryHsl
            const shadeS = Math.max(s, 0.45) // keep the ramp visibly saturated
            const [r1, g1, b1] = hslToRgb(h, shadeS, clamp01(l - 0.16))
            const [r2, g2, b2] = hslToRgb(h, shadeS, clamp01(l + 0.16))
            fromHex = toHex({ r: r1, g: g1, b: b1 })
            toHex_ = toHex({ r: r2, g: g2, b: b2 })
          }

          finish({ from: fromHex, to: toHex_ })
        } catch (e) {
          finish(fallback)
        }
      }
      img.onerror = () => finish(fallback)
      img.src = logoUrl
    } catch (e) {
      resolve(fallback)
    }
  })
}

/**
 * Apply the AI-accent gradient tokens. When a logo URL is provided, the
 * gradient is derived from that logo's own colours (via extractLogoGradient)
 * so AI-processing UI (extraction progress bar, "AI Suggest" button border,
 * AI badges) visually matches the org's brand. Falls back to the fixed
 * Xtract turquoise gradient when there's no logo, extraction fails, or
 * explicit fromHex/toHex overrides are passed in.
 */
function applyAiGradient(fromHex, toHex, logoUrl) {
  const root = document.documentElement
  if (fromHex || toHex) {
    root.style.setProperty('--ai-gradient-from', fromHex || AI_GRADIENT_FROM)
    root.style.setProperty('--ai-gradient-to', toHex || AI_GRADIENT_TO)
    return
  }
  // Apply the safe default immediately so UI never flashes unstyled while the
  // logo image loads/is sampled, then upgrade to the logo-derived gradient.
  root.style.setProperty('--ai-gradient-from', AI_GRADIENT_FROM)
  root.style.setProperty('--ai-gradient-to', AI_GRADIENT_TO)
  if (!logoUrl) return
  extractLogoGradient(logoUrl).then(({ from, to }) => {
    root.style.setProperty('--ai-gradient-from', from)
    root.style.setProperty('--ai-gradient-to', to)
  })
}

// ── Colour-theory palette derivation ─────────────────────────────────────────
// The original navy ramp (default look), as R G B triplets keyed by Tailwind step.
const BASE_NAVY = {
  50:  [240, 244, 248],
  100: [217, 226, 236],
  200: [188, 204, 220],
  300: [159, 179, 200],
  400: [130, 154, 177],
  500: [98, 125, 152],
  600: [72, 101, 129],
  700: [51, 78, 104],
  800: [30, 42, 59],   // == default Brand Colour #1e2a3b (the ramp anchor)
  900: [15, 23, 42],
}
const DEFAULT_PRIMARY_HEX = '#1e2a3b'

// Tailwind's stock gray-800/900/950 (the dark-mode surface fills used for the
// page background, header, cards and tables), as R G B triplets.
const BASE_SURFACE = {
  800: [31, 41, 55],
  900: [17, 24, 39],
  950: [3, 7, 18],
}

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '').trim()
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const num = parseInt(full, 16)
  if (Number.isNaN(num) || full.length !== 6) return [30, 42, 59]
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0, s = 0
  const d = max - min
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)); break
      case g: h = (b - r) / d + 2; break
      default: h = (r - g) / d + 4
    }
    h *= 60
  }
  return [h, s, l]
}

function hslToRgb(h, s, l) {
  h = ((h % 360) + 360) % 360
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  let r = 0, g = 0, b = 0
  if (h < 60) { r = c; g = x } else if (h < 120) { r = x; g = c }
  else if (h < 180) { g = c; b = x } else if (h < 240) { g = x; b = c }
  else if (h < 300) { r = x; b = c } else { r = c; b = x }
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ]
}

const clamp01 = (n) => Math.min(1, Math.max(0, n))

/**
 * Recolour the navy ramp from a brand primary colour.
 * Keeps each step's original *lightness* (so contrast + dark mode stay correct)
 * while shifting hue and scaling saturation to match the brand — a harmonious,
 * accessible monochromatic ramp. Default colour reproduces the original exactly.
 */
function applyDerivedNavyScale(primaryHex) {
  const root = document.documentElement

  // Default → restore exact original ramp.
  if (!primaryHex || String(primaryHex).toLowerCase() === DEFAULT_PRIMARY_HEX) {
    Object.entries(BASE_NAVY).forEach(([step, [r, g, b]]) => {
      root.style.setProperty(`--navy-${step}`, `${r} ${g} ${b}`)
    })
    return
  }

  const [dh, ds] = rgbToHsl(...BASE_NAVY[800])            // default anchor HSL
  const [ph, ps] = rgbToHsl(...hexToRgb(primaryHex))     // brand HSL
  const hueShift = ph - dh
  const satScale = ps / Math.max(ds, 0.01)

  Object.entries(BASE_NAVY).forEach(([step, [r, g, b]]) => {
    const [bh, bs, bl] = rgbToHsl(r, g, b)
    let ns = clamp01(bs * satScale)
    // Temper very light steps so vivid brands don't become neon pastels.
    if (bl > 0.85) ns = Math.min(ns, 0.40)
    const [nr, ng, nb] = hslToRgb(bh + hueShift, ns, bl)
    root.style.setProperty(`--navy-${step}`, `${nr} ${ng} ${nb}`)
  })
}

/**
 * Keep the dark-mode surface ramp neutral (stock Tailwind gray values), so
 * backgrounds/cards do not visually blend into the branded sidebar/nav color.
 */
function applyNeutralSurfaceScale() {
  const root = document.documentElement
  Object.entries(BASE_SURFACE).forEach(([step, [r, g, b]]) => {
    root.style.setProperty(`--surface-${step}`, `${r} ${g} ${b}`)
  })
}

/**
 * Apply theme tokens as CSS custom properties on :root
 * Each token becomes --token-{name} (e.g. --token-primary, --token-surface)
 */
function applyThemeTokens(tokens) {
  const root = document.documentElement
  const merged = { ...THEME_TOKEN_DEFAULTS, ...tokens }
  Object.entries(merged).forEach(([key, value]) => {
    // Convert underscore to hyphen for CSS: primary_hover → primary-hover
    const cssVar = `--token-${key.replace(/_/g, '-')}`
    let cssValue = value
    // The toggle "on" colour follows the org Brand Colour by default so it stays
    // consistent with the rest of the portal. A user can still override it
    // explicitly in the advanced token editor (value differs from the default).
    if (key === 'toggle_on' && value === THEME_TOKEN_DEFAULTS.toggle_on) {
      cssValue = 'var(--brand-primary, #1e2a3b)'
    }
    root.style.setProperty(cssVar, cssValue)
  })
}

/** Darken a hex color by a percentage (0-100) */
function darkenHex(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, (num >> 16) - Math.round(2.55 * percent))
  const g = Math.max(0, ((num >> 8) & 0x00ff) - Math.round(2.55 * percent))
  const b = Math.max(0, (num & 0x0000ff) - Math.round(2.55 * percent))
  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`
}

/** Lighten a hex color by mixing with white */
function lightenHex(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.min(255, (num >> 16) + Math.round((255 - (num >> 16)) * percent / 100))
  const g = Math.min(255, ((num >> 8) & 0x00ff) + Math.round((255 - ((num >> 8) & 0x00ff)) * percent / 100))
  const b = Math.min(255, (num & 0x0000ff) + Math.round((255 - (num & 0x0000ff)) * percent / 100))
  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`
}

/**
 * Update the tab icon. The Xtract favicons are a light/dark pair declared in
 * index.html (the browser picks one from its own colour scheme), so when the
 * wanted icon is one of those the pair is switched on; any other URL (a
 * client's or an uploaded favicon) replaces the pair with a single link.
 */
function applyFavicon(url) {
  try {
    const themed = document.querySelectorAll('link[data-xtract-favicon]')
    const useThemed = url ? XTRACT_FAVICON_URLS.has(url) : XTRACT_FAVICON_URLS.has(BRANDING_DEFAULTS.favicon_url)
    themed.forEach((link) => {
      if (!link.dataset.media) link.dataset.media = link.getAttribute('media') || ''
      if (useThemed) link.setAttribute('media', link.dataset.media)
      else link.setAttribute('media', 'not all')
    })

    let custom = document.querySelector("link[data-custom-favicon]")
    if (useThemed || !url) {
      // Without a custom URL the browser falls back to /favicon.ico.
      if (custom) custom.remove()
      return
    }
    if (!custom) {
      custom = document.createElement('link')
      custom.rel = 'icon'
      custom.setAttribute('data-custom-favicon', '')
      document.head.appendChild(custom)
    }
    custom.href = url
  } catch (e) {
    // Ignore DOM errors (e.g. insertBefore race conditions)
  }
}

export function BrandingProvider({ children }) {
  const { isAuthenticated, accessToken } = useAuth()
  const [branding, setBranding] = useState(BRANDING_DEFAULTS)
  const [themeTokens, setThemeTokens] = useState(THEME_TOKEN_DEFAULTS)
  const [themeTokenOverrides, setThemeTokenOverrides] = useState({})
  const [themeLibrary, setThemeLibrary] = useState([])
  const [loading, setLoading] = useState(false)

  // Fetch branding when authenticated
  useEffect(() => {
    if (!isAuthenticated || !accessToken) return

    setLoading(true)
    brandingApi.get()
      .then(res => {
        const data = res.data
        setBranding(data)
        applyBrandColors(data.primary_color, data.accent_color)
        applyAiGradient(null, null, data.logo_url)
        // Apply theme tokens
        const tokens = data.theme_tokens || THEME_TOKEN_DEFAULTS
        setThemeTokens(tokens)
        setThemeTokenOverrides(data.theme_token_overrides || {})
        applyThemeTokens(tokens)
        setThemeLibrary(Array.isArray(data.theme_library) ? data.theme_library : [])
        // Apply saved favicon on load so it persists across refreshes
        if (data.favicon_url) {
          applyFavicon(data.favicon_url)
        }
        if (data.app_name) {
          document.title = data.app_name
        }
      })
      .catch(err => {
        console.warn('Failed to load branding, using defaults:', err)
        applyBrandColors(BRANDING_DEFAULTS.primary_color, BRANDING_DEFAULTS.accent_color)
        applyAiGradient(null, null, BRANDING_DEFAULTS.logo_url)
        applyThemeTokens(THEME_TOKEN_DEFAULTS)
      })
      .finally(() => setLoading(false))
  }, [isAuthenticated, accessToken])

  // Apply defaults on mount for unauthenticated pages (login) and for the
  // brief window before the authenticated branding API call resolves.
  // This covers title/favicon too (not just colors) so refreshing never
  // shows Xtract's title/favicon in an environment whose default is
  // something else - only a real saved org override replaces this later.
  useEffect(() => {
    applyBrandColors(branding.primary_color, branding.accent_color)
    applyAiGradient(null, null, branding.logo_url)
    applyThemeTokens(themeTokens)
    document.title = branding.app_name
    applyFavicon(branding.favicon_url)
  }, [])

  // Remember the brand for the next start-up screen (index.html), which runs
  // before branding can be fetched. Only once the saved branding has arrived,
  // so a rebranded org isn't recorded as Xtract from the defaults.
  useEffect(() => {
    if (loading || branding === BRANDING_DEFAULTS) return
    try { localStorage.setItem(BOOT_BRAND_KEY, isXtractBranding(branding) ? 'xtract' : 'custom') } catch { /* storage unavailable */ }
  }, [branding, loading])

  const updateBranding = useCallback(async (updates) => {
    const res = await brandingApi.update(updates)
    const data = res.data
    setBranding(prev => ({ ...prev, ...data, is_default: false }))
    applyBrandColors(data.primary_color || updates.primary_color, data.accent_color || updates.accent_color)
    applyAiGradient(null, null, data.logo_url || updates.logo_url)
    applyFavicon(data.favicon_url)
    if (data.app_name) document.title = data.app_name
    // Update theme tokens - use response if available, fall back to input
    const responseTokens = data.theme_tokens
      ? (typeof data.theme_tokens === 'string' ? JSON.parse(data.theme_tokens) : data.theme_tokens)
      : null
    const inputTokens = updates.theme_tokens || null
    const tokenOverrides = responseTokens || inputTokens
    if (tokenOverrides) {
      const merged = { ...THEME_TOKEN_DEFAULTS, ...tokenOverrides }
      setThemeTokens(merged)
      setThemeTokenOverrides(tokenOverrides)
      applyThemeTokens(merged)
    }
    if (Array.isArray(data.theme_library)) setThemeLibrary(data.theme_library)
    return data
  }, [])

  // Saved themes: kept on the organization's branding row (not the browser),
  // so every admin sees and edits the same list. Written immediately - unlike
  // the rest of Settings, this isn't staged behind the page's Save button.
  // Throws on failure so the caller can show its own error and not update
  // its optimistic local state.
  const saveThemeLibrary = useCallback(async nextLibrary => {
    const res = await brandingApi.update({ theme_library: nextLibrary })
    const list = Array.isArray(res.data.theme_library) ? res.data.theme_library : nextLibrary
    setThemeLibrary(list)
    return list
  }, [])

  const resetBranding = useCallback(async () => {
    const res = await brandingApi.reset()
    const data = res.data
    const resetTokens = data.theme_tokens || data.theme_token_defaults || THEME_TOKEN_DEFAULTS
    const mergedTokens = { ...THEME_TOKEN_DEFAULTS, ...resetTokens }

    setBranding({ ...BRANDING_DEFAULTS, ...data, logo_url: null, favicon_url: null })
    applyBrandColors(data.primary_color || BRANDING_DEFAULTS.primary_color, data.accent_color || BRANDING_DEFAULTS.accent_color)
    applyAiGradient(null, null, BRANDING_DEFAULTS.logo_url)
    applyFavicon(null)
    document.title = data.app_name || BRANDING_DEFAULTS.app_name
    setThemeTokens(mergedTokens)
    setThemeTokenOverrides(data.theme_token_overrides || {})
    applyThemeTokens(mergedTokens)
    // Reset only clears the live theme - the saved theme library is untouched.
    if (Array.isArray(data.theme_library)) setThemeLibrary(data.theme_library)
  }, [])

  const uploadLogo = useCallback(async (file, type = 'logo') => {
    const res = await brandingApi.uploadLogo(file, type)
    const { url } = res.data
    // Don't update global state here - let Settings page hold it locally
    // until the user clicks Save. The save response will update global state.
    return url
  }, [])

  return (
    <BrandingContext.Provider value={{
      branding,
      loading,
      updateBranding,
      resetBranding,
      uploadLogo,
      themeTokens,
      themeTokenOverrides,
      themeLibrary,
      saveThemeLibrary,
      BRANDING_DEFAULTS,
      THEME_TOKEN_DEFAULTS,
    }}>
      {children}
    </BrandingContext.Provider>
  )
}

// True unless the organisation has replaced both Xtract's logo and its name
// (in Settings, or through this environment's default branding). Decides
// whether loaders may use the Xtract mark.
function isXtractBranding(branding) {
  const configured = (branding.logo_url || '').trim()
  const logo = (!configured || /(^|\/)favicon([.-]|$)/i.test(configured))
    ? BRANDING_DEFAULTS.logo_url
    : configured
  const logoChanged = Boolean(logo) && !XTRACT_LOGOS.has(logo)
  const name = (branding.app_name || '').trim().toLowerCase()
  const nameChanged = Boolean(name) && name !== 'xtract'
  return !(logoChanged && nameChanged)
}

// index.html reads this before any script loads to pick the start-up loader.
const BOOT_BRAND_KEY = 'xtract-boot-brand'

// Safe outside a BrandingProvider.
export function useIsXtractBrand() {
  const branding = useContext(BrandingContext)?.branding || BRANDING_DEFAULTS
  return isXtractBranding(branding)
}

export function useBranding() {
  const context = useContext(BrandingContext)
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider')
  }
  return context
}
